/* Landing visits: how many people a link sends to the landing page, and what
 * they do on its first screen.
 *
 * A visitor who starts training leaves an acquisition/{uid} record
 * (saveAcquisition.js). One who leaves without pressing anything leaves
 * nothing, except a Google Analytics hit if they said yes to Analytics, which
 * most people never do. So between an ad's click count and its starts there
 * was no telling a page that loses people from an audience that never cared.
 *
 * Each landing-page view writes one small record to landing_visits/{id},
 * built to need no consent, the same way Vigilia's landing visits are
 * (vigilia/shared/src/visits.ts):
 * - nothing is stored on the device and no identifier is made. The id is
 *   random per page view and lives only in this page's memory, so two visits
 *   by one person are two unrelated records;
 * - no IP address, no user agent: only the buckets below (phone or desktop,
 *   Android or iOS, which app's built-in browser);
 * - a visit is never joined to the account a visitor may go on to create.
 *   Visits and starts are set side by side by campaign, never one to one.
 * Kept 90 days (TTL on `expiresAt`); nothing in the app can read them back.
 *
 * Website only: the Android app's first screen is not where an ad lands.
 */
import { doc, serverTimestamp, setDoc, Timestamp, updateDoc } from "firebase/firestore";
import { db } from "./firebase.js";
import { attributionFromUrl } from "./attribution.js";

/** Visible this long, a visit counts as engaged: Google Analytics' own threshold, so the two compare. */
export const ENGAGED_MS = 10_000;
/** What a visitor did: started as a guest, with Google, with email, tapped the
 * Play badge, or scrolled down to the feature list under the first screen
 * ("more"), which is how the landing learns whether anyone reads past it.
 * Mirrored in firestore.rules. */
export const VISIT_ACTS = ["guest", "google", "email", "play", "more"];
export const VISIT_KEEP_DAYS = 90;
/** A tab left open all afternoon is not half an hour of reading. Mirrored in firestore.rules. */
export const MAX_MS = 30 * 60_000;
/* Visible time is saved as it crosses these, so a visit that ends without a
 * goodbye (the tab killed, the phone locked) still says whether it engaged.
 * 3 s because that is where most ad visits end: on the Reddit round's first
 * day 12 of 84 recorded 0 s, the goodbye write lost as the page closed, and
 * "left within 3 s" could only be inferred. */
export const SAVE_AT_MS = [3_000, ENGAGED_MS, 30_000, 120_000];

/* Crawlers by name, headless browsers and HTTP libraries. Stored, never counted. */
const BOT_RE = /bot\b|crawl|spider|slurp|headless|lighthouse|pagespeed|facebookexternalhit|embedly|python|curl\/|wget|okhttp|go-http|axios|node-fetch|phantomjs|puppeteer|playwright|selenium/i;

/* Apps whose built-in browser says so in its user agent. */
const APPS = [
  [/Reddit\//i, "reddit"],
  [/LinkedInApp/i, "linkedin"],
  [/FBAN|FBAV|FB_IAB|FBIOS/, "facebook"],
  [/Instagram/, "instagram"],
  [/Twitter/i, "x"],
  [/TikTok|musical_ly|BytedanceWebview/i, "tiktok"],
];

/** Device, system and in-app browser from a user agent. Only these buckets are kept, never the user agent. */
export function classifyUserAgent(ua) {
  const s = ua || "";
  const os = /iPhone|iPad|iPod/.test(s)
    ? "ios"
    : /Android/.test(s)
      ? "android"
      : /CrOS/.test(s)
        ? "chromeos"
        : /Macintosh|Mac OS X/.test(s)
          ? "mac"
          : /Windows/.test(s)
            ? "windows"
            : /Linux/.test(s)
              ? "linux"
              : "other";
  const device =
    /iPad|Tablet/.test(s) || (/Android/.test(s) && !/Mobile/.test(s)) ? "tablet" : /iPhone|iPod|Android|Mobi/.test(s) ? "phone" : "desktop";
  // An Android WebView (`; wv)`) is some app's built-in browser even when it doesn't say which.
  const app = APPS.find(([re]) => re.test(s))?.[1] ?? (/; wv\)/.test(s) ? "app" : undefined);
  return { device, os, ...(app ? { app } : {}), bot: !s || BOT_RE.test(s) };
}

/** The record a page view starts with. Absent fields are omitted, as Firestore wants. */
export function buildVisit({ search = "", referrer = "", ua = "", languages = [], webdriver = false, version } = {}) {
  const { at: _at, ...attribution } = attributionFromUrl(search, referrer);
  const { bot, ...kind } = classifyUserAgent(ua);
  const lang = String(languages?.[0] || "").toLowerCase().split("-")[0].slice(0, 8);
  return {
    path: "/",
    ...attribution,
    ...kind,
    ...(lang ? { lang } : {}),
    // navigator.webdriver: automation, whatever the user agent says.
    ...(bot || webdriver ? { bot: true } : {}),
    /* Which landing design the visit saw, so a change to it can be judged
     * against what came before. Absent on the records before it existed:
     * those are version 1. */
    ...(Number.isInteger(version) && version >= 1 ? { v: version } : {}),
    ms: 0,
    acts: [],
  };
}

/** 20 random letters and digits: the record's id, made in memory, never stored on the device. */
export function randomId(random = (n) => crypto.getRandomValues(new Uint8Array(n))) {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from(random(20), (b) => alphabet[b % alphabet.length]).join("");
}

let current = null;

/* One record per page load. The landing can mount twice in one load (React's
 * development double-mount, or signing out back to it), and that is still one
 * visit: the second start resumes the first. */
export function startLandingVisit({ version } = {}) {
  if (typeof window === "undefined" || typeof document === "undefined" || !db) return;
  if (current) current.resume();
  else current = createVisit(version);
}

/** The landing left the screen (the visitor started): stop counting its visible time. */
export function pauseLandingVisit() {
  current?.pause();
}

/** Something the visitor did on the landing: one of VISIT_ACTS. */
export function noteLandingAct(act) {
  current?.act(act);
}

function createVisit(version) {
  const ref = doc(db, "landing_visits", randomId());
  const row = buildVisit({
    version,
    search: window.location.search,
    referrer: document.referrer,
    ua: navigator.userAgent,
    languages: navigator.languages?.length ? navigator.languages : [navigator.language],
    webdriver: Boolean(navigator.webdriver),
  });
  const acts = [];
  let visibleMs = 0;
  let since = document.visibilityState === "visible" ? performance.now() : null;
  let paused = false;
  let saved = { ms: 0, acts: 0 };
  let timer = null;

  const elapsed = () => Math.min(MAX_MS, Math.round(visibleMs + (since === null ? 0 : performance.now() - since)));

  /* Best-effort throughout: a record that fails must never touch the page.
   * Writes are chained so an update never overtakes the create. */
  let chain = setDoc(ref, {
    ...row,
    at: serverTimestamp(),
    expiresAt: Timestamp.fromMillis(Date.now() + VISIT_KEEP_DAYS * 86_400_000),
  }).then(
    () => true,
    () => false,
  );

  const save = () => {
    const ms = elapsed();
    if (ms <= saved.ms && acts.length === saved.acts) return;
    saved = { ms: Math.max(ms, saved.ms), acts: acts.length };
    const update = { ms: saved.ms, acts: [...acts] };
    chain = chain.then((created) => (created ? updateDoc(ref, update).then(() => true, () => true) : false));
  };

  const tick = () => {
    const ms = elapsed();
    if (SAVE_AT_MS.some((mark) => ms >= mark && saved.ms < mark)) save();
  };
  const run = () => {
    if (!timer) timer = setInterval(tick, 1000);
  };
  const stop = () => {
    clearInterval(timer);
    timer = null;
  };

  const onVisibility = () => {
    if (document.visibilityState === "visible") {
      if (!paused && since === null) since = performance.now();
    } else {
      if (since !== null) visibleMs += performance.now() - since;
      since = null;
      save();
    }
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", save);
  run();

  return {
    act(name) {
      if (!VISIT_ACTS.includes(name) || acts.includes(name)) return;
      acts.push(name);
      save();
    },
    pause() {
      if (paused) return;
      if (since !== null) visibleMs += performance.now() - since;
      since = null;
      paused = true;
      stop();
      save();
    },
    resume() {
      if (!paused) return;
      paused = false;
      if (document.visibilityState === "visible") since = performance.now();
      run();
    },
  };
}
